import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import {
  MAX_COLABORADORES_POST,
  type MetaConexao,
  type PlataformaDePublicacaoDto,
  type PublicacaoDto,
} from "@base/contracts";
import { CalendarClock, Check, ImagePlus, Lock, X } from "lucide-react";
import { Botao, CampoDeTexto } from "@base/ui";
import { Button, Modal } from "@base/ui/renke";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SeletorDataHora } from "@/components/ui/seletor-data-hora";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { IconePlataforma } from "../../../shared/branding/icone-plataforma";
import { useAuth } from "../../../shared/auth/auth.contexto";
import {
  useContasSociais,
  useMetaConexao,
} from "../../clientes/application/use-clientes";
import { useListarMidiasOpcional } from "../../midias/application/use-midias";
import { MidiaAutenticada } from "../../referencias/presentation/midia-autenticada";
import { ModalEscolherBiblioteca } from "../../midias/presentation/modal-escolher-biblioteca";
import {
  useBuscarPerfilInstagram,
  useCriarEAgendarPublicacao,
  useEditarGrupoPublicacao,
  useObterPublicacao,
} from "../application/use-publicacoes";
import { podeEditarPublicacao } from "../domain/plano-edicao-grupo";
import { inferirTipoDeMidias } from "../domain/inferir-tipo-de-midias";
import { ListaMidiasOrdenavel } from "./lista-midias-ordenavel";
import { PreviewMidiasAgendamento } from "./preview-midias-agendamento";

/** "atuais" só existe na edição: mantém as mídias que o post já tem. */
type OrigemMidia = "computador" | "biblioteca" | "atuais";

type TipoPublicacao = "imagem" | "carrossel" | "reels";
type Passo = 1 | 2;

type ColaboradorCollab = {
  readonly username: string;
  readonly nome: string | null;
  readonly fotoUrl: string | null;
};

function iniciaisDoUsername(username: string): string {
  return username.slice(0, 2).toUpperCase();
}

function usernameLimpo(valor: string): string {
  return valor.trim().replace(/^@/, "");
}

/** Na edição a collab chega só com o @. Busca foto e nome se ainda não tiver. */
function CartaoColaborador({
  pessoa,
  onRemover,
}: {
  readonly pessoa: ColaboradorCollab;
  readonly onRemover: () => void;
}) {
  const perfil = useBuscarPerfilInstagram(pessoa.fotoUrl ? "" : pessoa.username);
  const encontrado = perfil.data?.encontrado ? perfil.data : null;
  const fotoUrl = pessoa.fotoUrl ?? encontrado?.fotoUrl ?? null;
  const nome = pessoa.nome ?? encontrado?.nome ?? null;

  return (
    <li className="flex items-center gap-3 rounded-xl border border-border px-3 py-3">
      <Avatar className="size-10 shrink-0 overflow-hidden">
        {fotoUrl ? (
          <AvatarImage
            src={fotoUrl}
            alt=""
            className="size-full object-cover"
          />
        ) : null}
        <AvatarFallback className="text-xs font-semibold">
          {iniciaisDoUsername(pessoa.username)}
        </AvatarFallback>
      </Avatar>
      <span className="min-w-0 flex-1 space-y-1">
        <span className="block font-medium leading-snug text-foreground">
          {nome ?? `@${pessoa.username}`}
        </span>
        {nome ? (
          <span className="block text-xs text-muted-foreground">
            @{pessoa.username}
          </span>
        ) : null}
      </span>
      <button
        type="button"
        aria-label={`Remover ${pessoa.username} dos colaboradores`}
        onClick={onRemover}
        className="cursor-pointer rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="size-4" aria-hidden="true" />
      </button>
    </li>
  );
}

function facebookComPaginaAtribuida(
  conexao: MetaConexao | undefined,
): boolean {
  if (!conexao || conexao.status !== "conectado" || !conexao.facebookPageId) {
    return false;
  }
  return (
    conexao.modoVinculo === "pagina" ||
    conexao.modoVinculo === "pagina_e_instagram"
  );
}

const REDES: ReadonlyArray<{
  readonly id: PlataformaDePublicacaoDto;
  readonly rotulo: string;
  readonly dica: string;
}> = [
  { id: "instagram", rotulo: "Instagram", dica: "Feed / Reels" },
  { id: "facebook", rotulo: "Facebook", dica: "via Meta" },
  { id: "twitter", rotulo: "X", dica: "texto até 280" },
  { id: "tiktok", rotulo: "TikTok", dica: "vídeo" },
];

type Props = {
  readonly aberto: boolean;
  readonly onFechar: () => void;
  /** Card da agenda sendo editado (uma publicação por rede). Sem isso, cria. */
  readonly edicao?: readonly PublicacaoDto[] | null;
};

function manterPainelParado(evento: { currentTarget: HTMLElement }): void {
  const painel = evento.currentTarget.closest(".rk-modal__panel");
  const rolagemJanela = window.scrollY;
  const topo = painel instanceof HTMLElement ? painel.scrollTop : null;
  const restaurar = () => {
    if (painel instanceof HTMLElement && topo !== null && painel.scrollTop !== topo) {
      painel.scrollTop = topo;
    }
    if (window.scrollY !== rolagemJanela) {
      window.scrollTo(0, rolagemJanela);
    }
  };
  if (painel instanceof HTMLElement) {
    painel.addEventListener("scroll", restaurar);
  }
  window.addEventListener("scroll", restaurar);
  window.setTimeout(() => {
    if (painel instanceof HTMLElement) {
      painel.removeEventListener("scroll", restaurar);
    }
    window.removeEventListener("scroll", restaurar);
    restaurar();
  }, 600);
}

const ROTULO_TIPO: Record<TipoPublicacao, string> = {
  imagem: "Imagem",
  carrossel: "Carrossel",
  reels: "Reels",
};

function ehTipoPublicacao(valor: string): valor is TipoPublicacao {
  return valor === "imagem" || valor === "carrossel" || valor === "reels";
}

/**
 * Modal em 2 passos: conteúdo (nome, legenda, mídias, redes) e depois agendamento.
 * Preview das mídias só no passo 1. Multi-rede gera N publicações.
 */
export function ModalNovoAgendamento({ aberto, onFechar, edicao }: Props) {
  const criarMutacao = useCriarEAgendarPublicacao();
  const editarMutacao = useEditarGrupoPublicacao();
  const contasSociais = useContasSociais();
  const meta = useMetaConexao();
  const editando = Boolean(edicao && edicao.length > 0);
  const criar = editando ? editarMutacao : criarMutacao;
  const plataformasTravadas = new Set(
    (edicao ?? [])
      .filter((p) => !podeEditarPublicacao(p))
      .map((p) => p.plataforma),
  );
  const representativo = edicao?.[0];
  const atual = useObterPublicacao(
    aberto && editando ? representativo?.id : undefined,
  );
  const { sessao } = useAuth();
  const idForm = useId();
  const idLegenda = useId();
  const idArquivos = useId();
  const idTipo = useId();
  const idSugestao = useId();
  const campoCollabRef = useRef<HTMLDivElement>(null);

  const [passo, setPasso] = useState<Passo>(1);
  const [nome, setNome] = useState("");
  const [legenda, setLegenda] = useState("");
  const [tipo, setTipo] = useState<TipoPublicacao>("imagem");
  const [plataformas, setPlataformas] = useState<PlataformaDePublicacaoDto[]>([
    "instagram",
  ]);
  const [quando, setQuando] = useState<Date | undefined>(undefined);
  const [colaboradores, setColaboradores] = useState<ColaboradorCollab[]>([]);
  const [colaboradorInput, setColaboradorInput] = useState("");
  const [usernameBusca, setUsernameBusca] = useState("");
  const [campoFocado, setCampoFocado] = useState(false);
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [origemMidia, setOrigemMidia] = useState<OrigemMidia>("computador");
  const [midiaIds, setMidiaIds] = useState<string[]>([]);
  const [modalBiblioteca, setModalBiblioteca] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const paginaFacebookAtribuida = facebookComPaginaAtribuida(
    meta.data?.conexao,
  );
  const redesConectadas = new Set(
    (contasSociais.data?.contas ?? [])
      .filter((conta) => conta.status === "conectado")
      .map((conta) => conta.plataforma)
      .filter(
        (plataforma) => plataforma !== "facebook" || paginaFacebookAtribuida,
      ),
  );
  const conferindoConexoes =
    !editando && (contasSociais.isLoading || meta.isLoading);
  const falhaNasConexoes = !editando && contasSociais.isError;
  const perfilQuery = useBuscarPerfilInstagram(usernameBusca);
  const biblioteca = useListarMidiasOpcional({
    tipo,
    plataformas,
  });

  // Ao abrir ou trocar de workspace, limpa mídias selecionadas (IDs de outro tenant).
  useEffect(() => {
    if (!aberto) return;
    setMidiaIds([]);
    setArquivos([]);
    setModalBiblioteca(false);
    setErro(null);
  }, [aberto, sessao?.tenantId]);

  // Edição: preenche com o que o card já tem.
  useEffect(() => {
    if (!aberto || !edicao || edicao.length === 0) return;
    const base = edicao[0]!;
    const doInstagram = edicao.find((p) => p.plataforma === "instagram");
    setPasso(1);
    setNome(base.nome);
    setLegenda(base.legenda);
    setTipo(base.tipo);
    setPlataformas(edicao.map((p) => p.plataforma));
    setQuando(base.agendarPara ? new Date(base.agendarPara) : undefined);
    setColaboradores(
      (doInstagram?.colaboradores ?? []).map((username) => ({
        username,
        nome: null,
        fotoUrl: null,
      })),
    );
    setOrigemMidia("atuais");
  }, [aberto, edicao]);

  // Novo post só pode usar canais conectados neste projeto.
  useEffect(() => {
    if (!aberto || editando || !contasSociais.data || meta.isLoading) return;
    const conectadas = REDES.filter((rede) =>
      redesConectadas.has(rede.id),
    ).map((rede) => rede.id);
    setPlataformas((atuais) => {
      const aindaConectadas = atuais.filter((id) => conectadas.includes(id));
      if (aindaConectadas.length > 0) return aindaConectadas;
      return conectadas[0] ? [conectadas[0]] : [];
    });
  }, [aberto, contasSociais.data, editando, meta.data, meta.isLoading]);

  function resetar() {
    setPasso(1);
    setNome("");
    setLegenda("");
    setTipo("imagem");
    setPlataformas(["instagram"]);
    setQuando(undefined);
    setColaboradores([]);
    setColaboradorInput("");
    setUsernameBusca("");
    setCampoFocado(false);
    setArquivos([]);
    setOrigemMidia("computador");
    setMidiaIds([]);
    setModalBiblioteca(false);
    setProgresso(0);
    setErro(null);
  }

  function aoFechar() {
    if (criar.isPending) return;
    resetar();
    onFechar();
  }

  function aoEscolherArquivos(lista: File[]) {
    setArquivos(lista);
    if (lista.length > 0) {
      setTipo(inferirTipoDeMidias(lista));
    }
  }

  function atualizarArquivos(proxima: File[]) {
    setArquivos(proxima);
    if (proxima.length > 0) {
      setTipo(inferirTipoDeMidias(proxima));
    }
  }

  function alternarRede(id: PlataformaDePublicacaoDto) {
    if (!editando && !redesConectadas.has(id)) return;
    setPlataformas((atual) => {
      if (atual.includes(id)) {
        if (atual.length === 1) return atual;
        if (plataformasTravadas.has(id)) return atual;
        if (id === "instagram") {
          setColaboradores([]);
          setColaboradorInput("");
          setUsernameBusca("");
          setCampoFocado(false);
        }
        return atual.filter((p) => p !== id);
      }
      return [...atual, id];
    });
  }

  function escolherColaborador(pessoa: ColaboradorCollab) {
    setErro(null);
    if (colaboradores.length >= MAX_COLABORADORES_POST) {
      setErro(`Collab aceita no máximo ${MAX_COLABORADORES_POST} contas.`);
      return;
    }
    if (
      colaboradores.some(
        (c) => c.username.toLowerCase() === pessoa.username.toLowerCase(),
      )
    ) {
      setColaboradorInput("");
      setUsernameBusca("");
      return;
    }
    setColaboradores((atual) => [...atual, pessoa]);
    setColaboradorInput("");
    setUsernameBusca("");
  }

  function removerColaborador(username: string) {
    setColaboradores((atual) => atual.filter((c) => c.username !== username));
  }

  function aoTeclarColaborador(evento: KeyboardEvent<HTMLInputElement>) {
    if (evento.key !== "Enter" && evento.key !== ",") return;
    evento.preventDefault();
    const perfil = perfilQuery.data;
    if (perfil?.encontrado) {
      escolherColaborador({
        username: perfil.username,
        nome: perfil.nome,
        fotoUrl: perfil.fotoUrl,
      });
    }
  }

  const slidesAtuais = (atual.data?.midias ?? []).flatMap((m) =>
    m.previewUrl
      ? [
          {
            chave: m.id,
            url: m.previewUrl,
            ehVideo: m.mimeType.startsWith("video/"),
          },
        ]
      : [],
  );
  const qtdMidias =
    origemMidia === "atuais"
      ? (representativo?.midias.length ?? 0)
      : origemMidia === "biblioteca"
        ? midiaIds.length
        : arquivos.length;
  const temMidia = qtdMidias > 0;
  const usernameDigitado = usernameLimpo(colaboradorInput);
  const sugestaoAberta = campoFocado && usernameDigitado.length >= 2;
  const buscandoSugestao =
    sugestaoAberta &&
    (usernameDigitado !== usernameBusca || perfilQuery.isFetching);
  const perfilSugestao = perfilQuery.data?.encontrado
    ? perfilQuery.data
    : null;

  useEffect(() => {
    const proximo = usernameLimpo(colaboradorInput);
    const timer = window.setTimeout(() => setUsernameBusca(proximo), 300);
    return () => window.clearTimeout(timer);
  }, [colaboradorInput]);

  useEffect(() => {
    const idsValidos = new Set((biblioteca.data?.itens ?? []).map((i) => i.id));
    setMidiaIds((atual) => atual.filter((id) => idsValidos.has(id)));
  }, [biblioteca.data]);

  function irParaAgendamento() {
    setErro(null);
    if (nome.trim().length < 2) {
      setErro("Informe o nome do post (mínimo 2 caracteres).");
      return;
    }
    if (!temMidia) {
      setErro("Selecione ao menos uma mídia válida.");
      return;
    }
    if (plataformas.length === 0) {
      setErro("Escolha ao menos uma rede.");
      return;
    }
    if (plataformas.includes("tiktok") && tipo === "carrossel") {
      setErro(
        "TikTok não aceita carrossel neste fluxo. Tire TikTok ou mude o tipo.",
      );
      return;
    }
    if (plataformas.includes("twitter") && legenda.length > 280) {
      setErro("No X a legenda fica em até 280 caracteres.");
      return;
    }
    setPasso(2);
  }

  async function aoAgendar(evento: FormEvent) {
    evento.preventDefault();
    if (passo !== 2) return;
    setErro(null);
    if (!quando) {
      setErro("Informe data e hora do agendamento.");
      return;
    }
    setProgresso(20);
    try {
      if (editando && edicao) {
        await editarMutacao.mutateAsync({
          itens: edicao,
          nome: nome.trim(),
          legenda,
          tipo,
          colaboradores: colaboradores.map((c) => c.username),
          plataformas,
          midias:
            origemMidia === "atuais"
              ? { origem: "atuais" }
              : origemMidia === "biblioteca"
                ? { origem: "biblioteca", midiaIds }
                : { origem: "computador", arquivos },
          agendarPara: quando.toISOString(),
        });
        setProgresso(100);
        resetar();
        onFechar();
        return;
      }
      await criarMutacao.mutateAsync({
        criar: {
          nome: nome.trim(),
          legenda,
          tipo,
          plataforma: plataformas[0] ?? "instagram",
          plataformas,
          colaboradores: colaboradores.map((c) => c.username),
        },
        ...(origemMidia === "biblioteca"
          ? { midiaIds }
          : { arquivos }),
        agendarPara: quando.toISOString(),
      });
      setProgresso(100);
      resetar();
      onFechar();
    } catch (causa) {
      setProgresso(0);
      setErro(
        causa instanceof Error
          ? causa.message
          : "Revise formato, proporção e tamanho das mídias.",
      );
    }
  }

  const podeAvancar =
    nome.trim().length >= 2 &&
    temMidia &&
    plataformas.length > 0 &&
    !criar.isPending;
  const podeAgendar = quando !== undefined && !criar.isPending;

  return (
    <>
    <Modal
      aberto={aberto}
      titulo={
        passo === 1 ? (editando ? "Editar post" : "Novo post") : "Quando sai"
      }
      descricao={
        passo === 1
          ? "Nome, redes, texto e mídia. A prévia entra ao lado."
          : `${nome.trim() || "Este post"} em ${plataformas.length} rede${plataformas.length === 1 ? "" : "s"}.`
      }
      onFechar={aoFechar}
      mostrarFechar={false}
      mostrarX
      fecharComEsc={false}
      className="max-w-[560px]"
      lateral={
        passo === 1 && temMidia ? (
          <PreviewMidiasAgendamento
            {...(origemMidia === "atuais"
              ? { slides: slidesAtuais }
              : origemMidia === "computador"
              ? { arquivos }
              : {
                  slides: (biblioteca.data?.itens ?? [])
                    .filter((item) => midiaIds.includes(item.id))
                    .map((item) => ({
                      chave: item.id,
                      url: item.previewUrl,
                      ehVideo: item.mimeType.startsWith("video/"),
                    })),
                })}
            tipo={tipo}
          />
        ) : undefined
      }
      rodape={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/*
            `key` distinta por passo: sem ela o React reaproveita o mesmo
            <button> e troca o type para "submit" no meio do clique em
            "Próximo" — o navegador enviava o formulário (na edição, salvava).
          */}
          {passo === 1 ? (
            <>
              <Button
                key="passo1-cancelar"
                type="button"
                variante="secondary"
                tamanho="sm"
                onClick={aoFechar}
                disabled={criar.isPending}
              >
                Cancelar
              </Button>
              <Button
                key="passo1-proximo"
                type="button"
                variante="brand"
                tamanho="sm"
                disabled={!podeAvancar}
                onClick={irParaAgendamento}
              >
                Próximo
              </Button>
            </>
          ) : (
            <>
              <Button
                key="passo2-voltar"
                type="button"
                variante="secondary"
                tamanho="sm"
                onClick={() => {
                  setErro(null);
                  setPasso(1);
                }}
                disabled={criar.isPending}
              >
                Voltar
              </Button>
              <Button
                key="passo2-salvar"
                type="submit"
                form={idForm}
                variante="brand"
                tamanho="sm"
                carregando={criar.isPending}
                disabled={!podeAgendar}
              >
                {editando ? "Salvar alterações" : "Salvar e agendar"}
              </Button>
            </>
          )}
        </div>
      }
    >
      <form
        id={idForm}
        className="mt-1 min-w-0 max-w-full space-y-6"
        onSubmit={(e) => void aoAgendar(e)}
      >
        <ol
          aria-label="Passos do agendamento"
          className="flex items-center gap-3"
        >
          {(
            [
              { numero: 1, rotulo: "Conteúdo" },
              { numero: 2, rotulo: "Quando" },
            ] as const
          ).map((etapa, indice) => {
            const feita = passo > etapa.numero;
            const atual = passo === etapa.numero;
            return (
              <li
                key={etapa.numero}
                className={cn(
                  "flex items-center gap-2.5",
                  indice === 0 && "flex-1",
                )}
                aria-current={atual ? "step" : undefined}
              >
                <span
                  className={cn(
                    "grid size-6 shrink-0 place-items-center rounded-full text-[11px] font-semibold tabular-nums transition-colors duration-200",
                    feita && "bg-primary text-primary-foreground",
                    atual && "bg-ink text-ink-foreground",
                    !feita &&
                      !atual &&
                      "border border-border-strong text-muted-foreground",
                  )}
                  aria-hidden="true"
                >
                  {feita ? <Check className="size-3.5" strokeWidth={3} /> : etapa.numero}
                </span>
                <span
                  className={cn(
                    "text-[13px] tracking-[-0.005em]",
                    atual || feita
                      ? "font-semibold text-foreground"
                      : "font-medium text-muted-foreground",
                  )}
                >
                  {etapa.rotulo}
                </span>
                {indice === 0 ? (
                  <span
                    className={cn(
                      "ml-1 h-px flex-1 transition-colors duration-300",
                      feita ? "bg-primary" : "bg-border",
                    )}
                    aria-hidden="true"
                  />
                ) : null}
              </li>
            );
          })}
        </ol>

        {passo === 1 ? (
          <>
            <div className="grid gap-4 min-[480px]:grid-cols-[1fr_10rem]">
              <div>
                <CampoDeTexto
                  rotulo="Nome do post"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  maxLength={120}
                  placeholder="Ex.: Reels de abertura da campanha"
                  required
                  autoFocus
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor={idTipo}>Tipo</Label>
                <Select
                  value={tipo}
                  onValueChange={(valor) => {
                    if (ehTipoPublicacao(valor)) setTipo(valor);
                  }}
                >
                  <SelectTrigger
                    id={idTipo}
                    className="h-11 rounded-lg"
                    aria-label="Tipo"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="imagem">Imagem</SelectItem>
                    <SelectItem value="carrossel">Carrossel</SelectItem>
                    <SelectItem value="reels">Reels / vídeo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-3">
              <Label>Redes</Label>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {REDES.map((rede) => {
                  const ativa = plataformas.includes(rede.id);
                  const travada = plataformasTravadas.has(rede.id);
                  const semPaginaFacebook =
                    rede.id === "facebook" &&
                    !editando &&
                    !paginaFacebookAtribuida &&
                    (meta.data?.conexao.status === "conectado" ||
                      (contasSociais.data?.contas.some(
                        (conta) =>
                          conta.plataforma === "facebook" &&
                          conta.status === "conectado",
                      ) ??
                        false));
                  const desconectada =
                    !editando && !redesConectadas.has(rede.id);
                  const indisponivel =
                    travada ||
                    desconectada ||
                    conferindoConexoes ||
                    falhaNasConexoes;
                  const motivo =
                    travada
                      ? "Já publicado nessa rede"
                      : conferindoConexoes
                        ? "Conferindo conexão"
                        : falhaNasConexoes
                          ? "Não foi possível conferir a conexão"
                          : semPaginaFacebook
                            ? "Atribua uma página do Facebook em Configurações"
                            : desconectada
                              ? "Conecte esta rede em Configurações"
                              : rede.dica;
                  return (
                    <button
                      key={rede.id}
                      type="button"
                      aria-pressed={ativa}
                      disabled={indisponivel}
                      title={motivo}
                      onClick={() => alternarRede(rede.id)}
                      className={cn(
                        "relative flex cursor-pointer flex-col items-start gap-3 rounded-xl border p-3 text-left transition-[background-color,border-color,box-shadow] duration-200",
                        "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/30",
                        ativa
                          ? "border-ink bg-sunken text-foreground shadow-[inset_0_0_0_1px_var(--ink)]"
                          : !indisponivel &&
                            "border-border bg-card text-foreground hover:border-border-strong hover:bg-sunken",
                        indisponivel &&
                          "cursor-not-allowed border-border bg-sunken text-muted-foreground opacity-55 shadow-none",
                      )}
                    >
                      <span
                        className={cn(
                          "grid size-8 place-items-center rounded-md transition-colors duration-200",
                          ativa ? "bg-card" : "bg-sunken",
                        )}
                      >
                        <IconePlataforma
                          plataforma={rede.id}
                          className="size-4"
                        />
                      </span>
                      <span className="absolute right-2.5 top-2.5">
                        {travada || desconectada ? (
                          <Lock className="size-3.5 text-muted-foreground" aria-hidden />
                        ) : ativa ? (
                          <span className="grid size-[18px] place-items-center rounded-full bg-primary text-primary-foreground">
                            <Check className="size-3" strokeWidth={3} aria-hidden />
                          </span>
                        ) : (
                          <span className="block size-[18px] rounded-full border-[1.5px] border-border-strong" />
                        )}
                      </span>
                      <span className="grid gap-0.5">
                        <span className="text-[13px] font-semibold leading-4 tracking-[-0.005em]">
                          {rede.rotulo}
                        </span>
                        <span className="text-[11px] leading-[14px] text-muted-foreground">
                          {semPaginaFacebook
                            ? "Sem página"
                            : desconectada
                              ? "Desconectada"
                              : rede.dica}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                {conferindoConexoes
                  ? "Conferindo os canais conectados..."
                  : falhaNasConexoes
                    ? "Não foi possível conferir os canais. Tente abrir o post novamente."
                    : plataformasTravadas.size > 0
                  ? "Redes com cadeado já publicaram e não mudam mais."
                  : redesConectadas.size === 0 && !editando
                    ? "Nenhum canal conectado. Conecte uma rede em Configurações para publicar."
                    : "Canais desconectados ficam bloqueados. Cada rede escolhida vira um post separado."}
              </p>
            </div>

            <div className="grid gap-3">
              <div className="flex items-baseline justify-between gap-3">
                <Label htmlFor={idLegenda}>Legenda</Label>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {legenda.length}/
                  {plataformas.includes("twitter") ? 280 : 2200}
                </span>
              </div>
              <Textarea
                id={idLegenda}
                value={legenda}
                onChange={(e) => setLegenda(e.target.value)}
                maxLength={plataformas.includes("twitter") ? 280 : 2200}
                rows={5}
                placeholder="O que o post vai dizer"
                className="min-h-32 cursor-text resize-y rounded-xl"
              />
            </div>

            {plataformas.includes("instagram") ? (
              <div className="grid gap-3">
                <Popover open={sugestaoAberta} modal={false}>
                  <PopoverAnchor asChild>
                    <div ref={campoCollabRef}>
                      <CampoDeTexto
                        rotulo="Colaboradores (collab)"
                        value={colaboradorInput}
                        onChange={(e) => setColaboradorInput(e.target.value)}
                        onFocus={() => setCampoFocado(true)}
                        onKeyDown={aoTeclarColaborador}
                        placeholder="@usuario"
                        disabled={
                          colaboradores.length >= MAX_COLABORADORES_POST
                        }
                        role="combobox"
                        aria-expanded={sugestaoAberta}
                        aria-controls={idSugestao}
                        aria-autocomplete="list"
                        aria-busy={buscandoSugestao}
                      />
                    </div>
                  </PopoverAnchor>
                  <PopoverContent
                    align="start"
                    className="w-80 max-w-[calc(100vw-2rem)] p-1"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onCloseAutoFocus={(e) => e.preventDefault()}
                    onInteractOutside={(e) => {
                      if (
                        e.target instanceof Node &&
                        campoCollabRef.current?.contains(e.target)
                      ) {
                        e.preventDefault();
                        return;
                      }
                      setCampoFocado(false);
                    }}
                  >
                    <div id={idSugestao} role="listbox" className="grid">
                      {buscandoSugestao ? (
                        <p className="px-3 py-3 text-sm text-muted-foreground">
                          Buscando...
                        </p>
                      ) : perfilSugestao ? (
                        <button
                          type="button"
                          role="option"
                          aria-selected="true"
                          className="flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors duration-200 hover:bg-muted"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            escolherColaborador({
                              username: perfilSugestao.username,
                              nome: perfilSugestao.nome,
                              fotoUrl: perfilSugestao.fotoUrl,
                            });
                          }}
                        >
                          <Avatar className="size-10 shrink-0 overflow-hidden">
                            {perfilSugestao.fotoUrl ? (
                              <AvatarImage
                                src={perfilSugestao.fotoUrl}
                                alt=""
                                className="size-full object-cover"
                              />
                            ) : null}
                            <AvatarFallback className="text-xs font-semibold">
                              {iniciaisDoUsername(perfilSugestao.username)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="min-w-0 flex-1 space-y-1">
                            <span className="block font-medium leading-snug text-foreground">
                              {perfilSugestao.nome ??
                                `@${perfilSugestao.username}`}
                            </span>
                            <span className="block text-xs text-muted-foreground">
                              @{perfilSugestao.username}
                            </span>
                          </span>
                        </button>
                      ) : (
                        <p className="px-3 py-3 text-sm text-muted-foreground">
                          Não achei essa conta. Confira o @.
                        </p>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
                {colaboradores.length > 0 ? (
                  <ul className="grid gap-3">
                    {colaboradores.map((pessoa) => (
                      <CartaoColaborador
                        key={pessoa.username}
                        pessoa={pessoa}
                        onRemover={() => removerColaborador(pessoa.username)}
                      />
                    ))}
                  </ul>
                ) : null}
                <p className="text-xs text-muted-foreground">
                  Digite o @ e escolha a pessoa na lista (até{" "}
                  {MAX_COLABORADORES_POST} contas). Ela ainda precisa aceitar
                  o convite no Instagram.
                </p>
              </div>
            ) : null}

            <div className="grid gap-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Label htmlFor={idArquivos}>Mídias</Label>
                <div className="flex flex-wrap gap-3">
                  {editando ? (
                    <Botao
                      type="button"
                      variante={origemMidia === "atuais" ? "tinta" : "contorno"}
                      tamanho="pequeno"
                      aria-pressed={origemMidia === "atuais"}
                      onClick={() => {
                        setOrigemMidia("atuais");
                        setArquivos([]);
                        setMidiaIds([]);
                        if (representativo) setTipo(representativo.tipo);
                      }}
                    >
                      Manter atuais
                    </Botao>
                  ) : null}
                  <Botao
                    type="button"
                    variante={origemMidia === "computador" ? "tinta" : "contorno"}
                    tamanho="pequeno"
                    aria-pressed={origemMidia === "computador"}
                    onClick={() => {
                      setOrigemMidia("computador");
                      setMidiaIds([]);
                    }}
                  >
                    Deste computador
                  </Botao>
                  <Botao
                    type="button"
                    variante={origemMidia === "biblioteca" ? "tinta" : "contorno"}
                    tamanho="pequeno"
                    aria-pressed={origemMidia === "biblioteca"}
                    onClick={() => {
                      setOrigemMidia("biblioteca");
                      setArquivos([]);
                      setModalBiblioteca(true);
                    }}
                  >
                    Biblioteca
                  </Botao>
                </div>
              </div>

              {origemMidia === "atuais" ? (
                <p className="text-sm text-muted-foreground">
                  {qtdMidias} mídia{qtdMidias === 1 ? "" : "s"} atual
                  {qtdMidias === 1 ? "" : "is"} · prévia ao lado. Para trocar,
                  escolha do computador ou da biblioteca.
                </p>
              ) : origemMidia === "computador" ? (
                <>
                  <div
                    className={cn(
                      "group/drop relative flex cursor-pointer items-center justify-center gap-3 rounded-xl border-[1.5px] border-dashed border-border-strong bg-card text-center transition-colors duration-200 hover:border-primary hover:bg-primary-tint/40",
                      arquivos.length > 0
                        ? "px-4 py-3"
                        : "min-h-36 flex-col px-6 py-8",
                    )}
                  >
                    <span
                      className={cn(
                        "grid shrink-0 place-items-center rounded-lg bg-primary-soft text-foreground transition-transform duration-200 group-hover/drop:scale-105",
                        arquivos.length > 0 ? "size-8" : "size-11",
                      )}
                      aria-hidden="true"
                    >
                      <ImagePlus className={arquivos.length > 0 ? "size-4" : "size-5"} />
                    </span>
                    <span className="grid gap-1">
                      <span className="text-sm font-semibold text-foreground">
                        {arquivos.length > 0
                          ? `${arquivos.length} arquivo${arquivos.length === 1 ? "" : "s"} · clique para trocar`
                          : "Arraste os arquivos ou clique para escolher"}
                      </span>
                      {arquivos.length === 0 ? (
                        <span className="text-xs text-muted-foreground">
                          JPG, PNG, MP4 ou MOV · carrossel até 20
                        </span>
                      ) : null}
                    </span>
                    <input
                      id={idArquivos}
                      type="file"
                      accept="image/jpeg,image/png,video/mp4,video/quicktime"
                      multiple
                      className="absolute inset-0 cursor-pointer opacity-0"
                      onMouseDown={manterPainelParado}
                      onFocus={manterPainelParado}
                      onChange={(e) =>
                        aoEscolherArquivos(Array.from(e.target.files ?? []))
                      }
                    />
                  </div>
                  {arquivos.length > 0 ? (
                    <>
                      <ListaMidiasOrdenavel
                        arquivos={arquivos}
                        onMudar={atualizarArquivos}
                      />
                      <p className="text-xs text-muted-foreground">
                        Arraste ou use as setas para mudar a ordem do carrossel.
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      A prévia entra ao lado quando tiver arquivo.
                    </p>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  {midiaIds.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      Abra a biblioteca e escolha os arquivos deste projeto.
                    </p>
                  ) : (
                    <ul className="flex gap-3 overflow-x-auto pb-1">
                      {midiaIds.map((id) => {
                        const item = (biblioteca.data?.itens ?? []).find(
                          (m) => m.id === id,
                        );
                        if (!item) return null;
                        return (
                          <li
                            key={id}
                            className="w-20 shrink-0 overflow-hidden rounded-lg border border-input"
                          >
                            <div className="aspect-square bg-muted">
                              <MidiaAutenticada
                                src={item.capaUrl}
                                tipo="image"
                                alt=""
                                className="size-full object-cover"
                              />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <Button
                    type="button"
                    variante="secondary"
                    tamanho="sm"
                    className="cursor-pointer"
                    onClick={() => setModalBiblioteca(true)}
                  >
                    {midiaIds.length === 0
                      ? "Abrir biblioteca"
                      : "Escolher de novo"}
                  </Button>
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="rounded-xl border border-border-subtle bg-sunken/50 p-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                Resumo
              </p>
              <p className="mt-1.5 text-base font-semibold leading-6 tracking-[-0.005em] text-foreground">
                {nome.trim()}
              </p>
              <p className="mt-1 line-clamp-2 text-[13px] leading-[18px] text-muted-foreground">
                {legenda.trim() || "Sem legenda"}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {plataformas.map((p) => (
                  <span
                    key={p}
                    className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-xs font-medium text-foreground-secondary"
                  >
                    <IconePlataforma plataforma={p} className="size-3.5" />
                    {REDES.find((r) => r.id === p)?.rotulo ?? p}
                  </span>
                ))}
                <span className="inline-flex h-7 items-center rounded-md border border-border bg-card px-2.5 text-xs font-medium text-foreground-secondary">
                  {ROTULO_TIPO[tipo]} · {qtdMidias} mídia{qtdMidias === 1 ? "" : "s"}
                </span>
              </div>
            </div>

            <div className="grid gap-2">
              <SeletorDataHora
                rotulo="Data e hora"
                value={quando}
                onChange={setQuando}
                bloquearPassado
                placeholder="Escolher data e hora"
              />
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <CalendarClock className="size-3.5" aria-hidden />
                Horário do seu navegador. O post sai sozinho nesse momento.
              </p>
            </div>

            {progresso > 0 && progresso < 100 ? (
              <Progress value={progresso} aria-label="Progresso do upload" />
            ) : null}
          </>
        )}

        {erro ? (
          <Alert variant="destructive">
            <AlertDescription>{erro}</AlertDescription>
          </Alert>
        ) : null}
      </form>
    </Modal>
    <ModalEscolherBiblioteca
      aberto={aberto && modalBiblioteca}
      itens={biblioteca.data?.itens ?? []}
      selecionados={midiaIds}
      carregando={biblioteca.isLoading}
      onAlternar={(id) => {
        setMidiaIds((atual) =>
          atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id],
        );
      }}
      onConfirmar={() => setModalBiblioteca(false)}
      onFechar={() => setModalBiblioteca(false)}
    />
    </>
  );
}
